import type { ReactNode } from "react";
import React, { createContext, useContext, useState } from "react";

// context provider for finance report state and callbacks
// uses stable object references that only update when primitive values change
// this prevents infinite loops while keeping all state in React Context (no Redux needed)
type ReportActionsState = {
  // primitives (stable comparisons)
  reportType: string | null;
  isGenerating: boolean;
  isLoadingReportData: boolean;
  showReportTable: boolean;
  page: number;
  selectedRowsCount: number;
  canShowBatchActions: boolean;
  lastRunTime: Date | null;
  totalRecords: number | null;
  // serialized data (stable string comparisons)
  reportDataJson: string | null;
  reportMetaJson: string | null;
  selectedRowsArray: (string | number)[];
  // stable callbacks
  onViewReport: (() => void) | null;
  onGenerateReport: (() => void) | null;
  onGenerateLedesReport: (() => void) | null;
  onShareReport: (() => void) | null;
  onResetReport: (() => void) | null;
  setSelectedRows: ((rows: Set<string | number>) => void) | null;
  // Section 111 select-all mode
  isSelectAllMode: boolean;
  setIsSelectAllMode: ((value: boolean) => void) | null;
};

type ReportActionsContextType = {
  state: ReportActionsState;
  setState: (state: ReportActionsState) => void;
};

const defaultState: ReportActionsState = {
  reportType: null,
  isGenerating: false,
  isLoadingReportData: false,
  showReportTable: false,
  page: 1,
  selectedRowsCount: 0,
  canShowBatchActions: false,
  lastRunTime: null,
  totalRecords: null,
  reportDataJson: null,
  reportMetaJson: null,
  selectedRowsArray: [],
  onViewReport: null,
  onGenerateReport: null,
  onGenerateLedesReport: null,
  onShareReport: null,
  onResetReport: null,
  setSelectedRows: null,
  isSelectAllMode: false,
  setIsSelectAllMode: null,
};

const ReportActionsContext = createContext<
  ReportActionsContextType | undefined
>(undefined);

// context provider for finance report actions
// stores both state and callbacks in a single stable object
export const ReportActionsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<ReportActionsState>(defaultState);

  // memoize the context value to prevent unnecessary re-renders
  // only updates when state reference changes (which only happens when setState is called)
  const value = React.useMemo(() => ({ state, setState }), [state]);

  return (
    <ReportActionsContext.Provider value={value}>
      {children}
    </ReportActionsContext.Provider>
  );
};

// hook to access report actions state
export const useReportActions = () => {
  const context = useContext(ReportActionsContext);
  if (!context) {
    throw new Error(
      "useReportActions must be used within ReportActionsProvider",
    );
  }
  return context;
};
